<template>
  <pdl-loading v-if="hasTechData" :is-loading="!isPageLoaded">
    <div qaid="tech-info-container" class="mx-auto px-2 max-w-screen-xxl text-md">
      <section v-if="b2b" class="text-center">
        <pdl-section-header size="xl" :is-dividing="true" class="my-4">
          <template #content>
            <pdl-heading :level="1" qaid="tech__parts-header">{{ techPartsHeader }}</pdl-heading>
          </template>
        </pdl-section-header>
      </section>
      <!-- Product section -->
      <section v-if="hasRefLinks || isTechPage">
        <pdl-section-header size="md">
          <template #content>
            <pdl-heading :level="2" qaid="tech__product-name-header">
              {{ productModelYear }} {{ computedProductName }}
            </pdl-heading>
          </template>
        </pdl-section-header>
        <div class="gap-10 md:grid md:grid-cols-12 mt-3 mb-6">
          <div class="md:col-span-6 lg:col-span-4">
            <div qaid="tech__product-code" class="font-medium text-gray-60 mb-1">
              {{ $t('garage.bike.type') }} {{ productCode }}
            </div>
            <img :src="productImage" alt="" />
          </div>

          <div class="md:col-span-6 lg:flex lg:col-span-8 flex-wrap gap-8">
            <div v-if="references && references.length" qaid="tech__references-link">
              <div class="font-medium text-gray-60 mb-1">{{ $t('text.references') }}</div>

              <ul class="mb-2">
                <li v-for="reference in references" :key="reference.title" class="icon-item mb-1">
                  <pdl-icon :class="{invisible: !reference.assetLocation}" name="description" qaid="ref-link-icon" />
                  <ref-link
                    class="underline flex-1"
                    :title="reference.title"
                    :link="reference.link"
                    :asset-type="reference.assetType"
                    :asset-location="reference.assetLocation"
                    :target="reference.target"
                  />
                </li>
              </ul>
            </div>

            <div v-if="videos && videos.length">
              <div class="font-medium text-gray-60 mb-1">
                {{ $t('text.videos') }}
              </div>
              <ul class="mb-2">
                <li v-for="video in videos" :key="video.title" class="icon-item mb-1">
                  <pdl-icon name="play_circle_filled" :label="$t('text.watch.video')" />
                  <ref-link
                    class="underline flex-1"
                    :title="video.title"
                    :link="video.link"
                    :asset-type="video.assetType"
                    :asset-location="video.assetLocation"
                    :target="reference.target"
                  />
                </li>
              </ul>
            </div>
            <div v-if="isTechPage">
              <div class="font-medium text-gray-60 mb-1">
                {{ $t('text.url') }}
              </div>
              <ul class="mb-2">
                <li qaid="tech__product-page-link" class="icon-item mb-1">
                  <pdl-icon name="arrow_forward" />
                  <a :href="`${encodedContextPath}/p/${productCode}/`" class="underline flex-1">{{
                    $t('techProdSupport.B2B.visitProductPage')
                  }}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <!-- Replacement parts section -->
      <section v-if="hasSpecItems" qaid="tech__related-parts-section" class="lg:grid grid-cols-12 gap-5 my-5">
        <div class="lg:col-span-3">
          <pdl-section-header size="md">
            <template #content>
              <pdl-heading :level="2">{{ $t('techProdSupport.B2B.findParts') }}</pdl-heading>
            </template>
          </pdl-section-header>
          <div class="mb-1">
            {{ $t('techProdSupport.B2B.findParts.selectCategory') }}
          </div>
          <div class="mb-2">
            <fieldset>
              <tech-info-variant :product="product" @sku-selected="skuSelected = $event" />
              <div class="buttons">
                <pdl-button primary block @click="findRelatedParts">
                  {{ $t('techProdSupport.B2B.findParts') }}
                </pdl-button>
                <pdl-button secondary block icon="list" class="hide-for-large" @click="toggleFacetDrawer">
                  {{ $t('text.filterParts') }} {{ filterCount }}
                </pdl-button>
              </div>
            </fieldset>
          </div>

          <div id="facetContainer" class="b2b-techinfo-filters hidden lg:block lg:mt-4">
            <pdl-section-header size="md" :is-dividing="true">
              <template #content>
                <pdl-heading>{{ $t('search.nav.refinements') }} {{ filterCount }}</pdl-heading>
              </template>
            </pdl-section-header>
            <pdl-collapse v-if="filterGroups?.length" :value="['filter-Parts']" qaid="tech-info-filter-collapse">
              <filter-group />
            </pdl-collapse>
          </div>
        </div>

        <div class="lg:col-span-9">
          <div>
            <div class="flex justify-between mb-1 font-medium">
              <span>{{ filteredItems.length }} {{ $t('text.results') }}</span>
              <span class="text-gray-60">{{ $t('text.account.savedLists.sku') }} {{ skuSelected }}</span>
            </div>

            <div v-for="item in displayedItems" :key="item.id" class="bg-gray-05 p-3 md:pb-2 mb-1">
              <dl class="description-list gap-x-2 md:grid grid-cols-4">
                <dt qaid="tech-info-item-type">{{ $t('text.itemType') }}</dt>
                <dd class="col-span-3">{{ item.partName }}</dd>

                <template v-if="item.description">
                  <dt>
                    {{ $t('productCompare.description') }}
                  </dt>
                  <dd class="col-span-3">
                    {{ item.description }}
                  </dd>
                </template>

                <template v-if="item.originalItem.length > 0 || item.serviceMessage">
                  <dt>
                    {{ $t('text.originalItem') }}
                  </dt>

                  <dd class="col-span-3">
                    <a
                      v-if="item.originalItem.length > 0"
                      :href="`${encodedContextPath}${item.originalItem[0].url}`"
                      class="underline"
                      >{{ item.originalItem[0].sku }}</a
                    >
                    <span v-else>{{ item.serviceMessage }}</span>
                  </dd>
                </template>

                <template v-if="showMfrPartNumber(item)">
                  <dt>
                    {{ $t('text.mfgPartNumber') }}
                  </dt>

                  <dd class="col-span-3">
                    {{ item.originalItem[0].mfrPartNumber }}
                  </dd>
                </template>

                <template v-if="item.alternativeItems.length > 0 || item.serviceMessage">
                  <template v-if="item.alternativeItems.length > 0">
                    <template v-for="(altItem, altItemIndex) in item.alternativeItems" :key="altItem.sku">
                      <dt>
                        <span v-if="altItemIndex === 0">{{ $t('text.alternativeItems') }}</span>
                      </dt>
                      <dd class="col-span-3">
                        <a :href="`${encodedContextPath}${altItem.url}`" class="underline">{{ altItem.sku }}</a>
                      </dd>
                    </template>
                  </template>
                  <template v-else>
                    <dt>
                      {{ $t('text.alternativeItems') }}
                    </dt>
                    <dd class="col-span-3">
                      {{ item.serviceMessage }}
                    </dd>
                  </template>
                </template>
              </dl>
            </div>

            <div v-if="filteredItems.length > resultsSize" class="buttons">
              <pdl-button primary block @click="resultsSize += showMoreIncrement">
                {{ $t('review.show.more') }}
              </pdl-button>
            </div>

            <div v-else-if="filteredItems.length <= resultsSize && showMoreIncrement < resultsSize" class="buttons">
              <pdl-button primary block @click="resultsSize = showMoreIncrement">
                {{ $t('text.showFewer') }}
              </pdl-button>
            </div>
          </div>
        </div>
      </section>
    </div>
    <teleport to="body">
      <pdl-drawer placement="top" :style="drawerStyle" :is-open="isShowingDrawer">
        <template #content>
          <pdl-button secondary class="mt-2" @click="toggleFacetDrawer">
            <div class="flex content-center justify-between w-full">
              <div class="flex items-center justify-center w-full">
                <pdl-icon class="mr-1" name="list" />
                {{ $t('search.nav.refinements') }} {{ filterCount }}
              </div>
              <div class="grow-0">
                <pdl-icon name="close" :label="$t('popup.close')" />
              </div>
            </div>
          </pdl-button>
          <pdl-button v-if="filterCount" primary block class="mt-1" @click="toggleFacetDrawer">
            {{ $t('search.nav.done.button') }}
          </pdl-button>
          <pdl-collapse v-if="filterGroups?.length" :value="['filter-Parts']" qaid="tech-info-filter-collapse">
            <filter-group />
          </pdl-collapse>
        </template>
      </pdl-drawer>
    </teleport>
  </pdl-loading>
</template>

<script>
import {mapGetters, mapMutations, mapState} from 'vuex';
import {ProductApi} from '@/api/product';
import {PdlIcon} from '@pedal/pdl-icon';
import TechInfoVariant from '@/components/containers/tech-info/TechInfoVariant.vue';
import {PdlCollapse} from '@pedal/pdl-collapse';
import FilterGroup from '@/components/FilterGroup';
import RefLink from '@/components/ref-link/RefLink';
import {PdlSectionHeader, PdlHeading} from '@pedal/pdl-section-header';
import {PdlLoading} from '@pedal/pdl-loading';
import {ViewSize} from '@/constants/view-breakpoints';
import {PdlDrawer} from '@pedal/pdl-drawer';
import {PdlButton} from '@pedal/pdl-button';
import {Cloudinary} from '@/utils/image/cloudinary-helpers';

export default {
  components: {
    PdlIcon,
    TechInfoVariant,
    PdlCollapse,
    FilterGroup,
    RefLink,
    PdlSectionHeader,
    PdlHeading,
    PdlLoading,
    PdlDrawer,
    PdlButton,
  },

  props: {
    isPdp: Boolean,
    baseProductCode: {
      type: String,
      default: null,
    },
    baseProductImage: {
      type: String,
      default: null,
    },
    productModelYear: {
      type: String,
      default: null,
    },
    productName: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      specItems: [],
      skuSelected: '',
      productImage: '',
      productCode: '',
      resultsSize: 15,
      showMoreIncrement: 15,
      references: [],
      videos: [],
      hasFetchedTechData: false,
      loadingStatus: {
        areReferencesLoaded: false,
        areSpecsLoaded: false,
      },
      isDrawerOpen: false,
    };
  },

  computed: {
    ...mapState('storage', ['bikeTechSKU']),
    ...mapState('backend', ['b2b', 'encodedContextPath']),
    ...mapState('productDetails', ['product', 'isBike', 'variantCode', 'pdpSku']),
    ...mapState('filterGroup', ['filterGroups']),
    ...mapState('global', ['headerHeight']),
    ...mapGetters('familyDetails', ['isFDP']),
    ...mapGetters('viewSize', ['viewBreakpointMoreThan']),
    ...mapGetters('filterGroup', ['selectedItems']),

    computedProductName() {
      return this.productName || this.product?.name;
    },
    displayedItems() {
      return this.filteredItems.slice(0, this.resultsSize);
    },
    techPartsHeader() {
      if (this.isBike) {
        return this.$t('techProdSupport.B2B.bike');
      } else {
        return this.$t('techProdSupport.B2B.partsAccessories');
      }
    },
    hasSpecItems() {
      return !!this.specItems?.length;
    },
    hasRefLinks() {
      return !!this.references?.length;
    },
    hasTechData() {
      return (this.isPageLoaded && (this.hasRefLinks || this.hasSpecItems)) || !this.isPageLoaded;
    },
    isTechPage() {
      return !this.isPdp && !this.isFDP && this.b2b;
    },
    isShowingDrawer() {
      return !this.viewBreakpointMoreThan(ViewSize.MEDIUM) && this.isDrawerOpen;
    },
    isPageLoaded() {
      const keys = Object.keys(this.loadingStatus);

      const isLoaded = keys.every((key) => this.loadingStatus[key]);
      return this.isTechPage || isLoaded;
    },
    filteredItems() {
      if (this.selectedItems?.length) {
        return this.specItems.filter((specItem) =>
          this.selectedItems.some((selected) => selected.id === specItem.partId)
        );
      }
      return this.specItems;
    },
    filterCount() {
      return this.filteredItems.length ? `(${this.filteredItems.length})` : '';
    },
    drawerStyle() {
      return {
        top: `${this.headerHeight}px`,
      };
    },
  },

  watch: {
    skuSelected() {
      if (!this.productImage) this.findParts();
    },
    variantCode: {
      handler(newValue) {
        this.skuSelected = newValue;
      },
      immediate: true,
    },
    bikeTechSKU: {
      handler(newValue) {
        if (this.isTechPage) {
          this.skuSelected = newValue;
        }
      },
      immediate: true,
    },
    product: {
      async handler(newValue, oldValue) {
        if (!newValue) return;
        if (newValue !== oldValue) {
          this.productCode = newValue?.code;
          this.loadingStatus.areReferencesLoaded = false;
          try {
            const techData = await ProductApi.fetchProductTechData(this.productCode);
            this.references = techData.references;
            this.videos = techData.videos;
          } catch (err) {
            console.error(err);
            this.clearData();
          } finally {
            this.loadingStatus.areReferencesLoaded = true;
          }
        }
      },
      immediate: true,
    },
  },

  async mounted() {
    if (this.isTechPage) {
      try {
        const productData = await ProductApi.loadProduct(this.bikeTechSKU, this.b2b);
        this.setProduct(productData);
      } catch (err) {
        console.error(err, 'There was an error fetching product data...');
      }
    }
  },
  methods: {
    ...mapMutations('storage', ['setBikeTechSKU']),
    ...mapMutations('productDetails', ['setProduct']),
    ...mapMutations('filterGroup', ['setFilterGroups', 'clearFilters']),

    toggleFacetDrawer() {
      this.isDrawerOpen = !this.isDrawerOpen;
    },

    showMfrPartNumber(displayedItem) {
      return displayedItem.originalItem?.length > 0 && displayedItem.originalItem[0]?.mfrPartNumber;
    },
    findParts() {
      this.retrieveSpecInfo(this.skuSelected);
    },

    findRelatedParts() {
      this.findParts();
      this.clearFilters();
    },

    async retrieveSpecInfo(variantSku) {
      if (variantSku?.length) {
        this.loadingStatus.areSpecsLoaded = false;
        try {
          const data = await ProductApi.fetchProductSpecInfo(variantSku);
          if (data?.filters?.length) {
            const filter = {
              label: this.$t('text.partsAccessories'),
              items: data.filters[0].filters.map((filterItem) => ({
                id: filterItem.id,
                text: filterItem.specItemName,
              })),
            };
            this.setFilterGroups([filter]);
            this.specItems = data.specItems;
            this.productImage = data.productImage;
            this.productCode = data.sku;
            this.filteredItems = this.specItems;
          } else {
            this.clearData();
          }
        } catch (error) {
          this.clearData();
        } finally {
          this.loadingStatus.areSpecsLoaded = true;
        }
      }
    },

    clearData() {
      this.setFilterGroups([]);
      this.specItems = [];
      this.productImage =
        Cloudinary.createAssetUrl(this.product?.images?.[Object.keys(this.product.images)[0]]?.[0]?.assetId) ||
        this.baseProductImage;
      this.productCode = this.product?.code || this.baseProductCode;
    },
  },
};
</script>
